import type { QuizPlayer } from 'types/quiz-player';
import type { ReadinessTeamMember } from 'types/readiness';

const findPlayerById = (
  team: ReadinessTeamMember[],
  id: string | null
): QuizPlayer | null => {
  if (!id) return null;
  return team.find((member) => member.player?.id === id)?.player ?? null;
};

export type PitchFlagType =
  | 'aiCaptain'
  | 'captainMatch'
  | 'yourCaptain'
  | 'yourSell'
  | 'aiSell';

export type SellNoteVariant = 'agree' | 'differ' | 'noSell';

export type VerdictPitchFlags = {
  /** One flag per flagged player id (collisions already resolved). */
  flagsByPlayerId: Map<string, PitchFlagType>;
  aiCaptain: QuizPlayer | null;
  aiSell: QuizPlayer | null;
  sellNoteVariant: SellNoteVariant;
};

export type VerdictPitchFlagsInput = {
  team: ReadinessTeamMember[];
  userCaptain: QuizPlayer | null;
  /** The player the user chose to move out; null when rolling or undecided. */
  userSell: QuizPlayer | null;
  /** The AI's captain / sell from the team-rating derived block. */
  aiCaptainId: string | null;
  aiSellPlayerId: string | null;
};

/**
 * Derives the verdict-pitch flags per spec 6.6. Collision priority:
 * captain flags > YOUR SELL > AI'S SELL — a player keeps only its
 * highest-priority flag. The AI's captain and sell come from the server's
 * derived block (ids), resolved against the squad here.
 */
export const deriveVerdictPitchFlags = ({
  team,
  userCaptain,
  userSell,
  aiCaptainId,
  aiSellPlayerId
}: VerdictPitchFlagsInput): VerdictPitchFlags => {
  const flagsByPlayerId = new Map<string, PitchFlagType>();

  const aiCaptain = findPlayerById(team, aiCaptainId);
  const aiSell = findPlayerById(team, aiSellPlayerId);

  if (aiCaptain && userCaptain && userCaptain.id === aiCaptain.id) {
    flagsByPlayerId.set(aiCaptain.id, 'captainMatch');
  } else {
    if (aiCaptain) flagsByPlayerId.set(aiCaptain.id, 'aiCaptain');
    if (userCaptain) flagsByPlayerId.set(userCaptain.id, 'yourCaptain');
  }

  if (userSell && !flagsByPlayerId.has(userSell.id)) {
    flagsByPlayerId.set(userSell.id, 'yourSell');
  }

  if (aiSell && !flagsByPlayerId.has(aiSell.id)) {
    flagsByPlayerId.set(aiSell.id, 'aiSell');
  }

  const sellNoteVariant: SellNoteVariant = !userSell
    ? 'noSell'
    : aiSell && userSell.id === aiSell.id
    ? 'agree'
    : 'differ';

  return { flagsByPlayerId, aiCaptain, aiSell, sellNoteVariant };
};
